/**
 * default-layout.ts — jeden domyślny układ pulpitu dla nowych użytkowników: kompozycja
 * z Figmy „10 · Dashboard examples" (Workflow 3.0), złożona z kafelków „08.6 · Cards & widgets".
 *
 * Fabryka bierze `t()` (zero hardcoded stringów — tytuły idą przez i18n). Id widżetów są
 * stabilne (nie `randomUUID`), żeby domyślny układ był deterministyczny i dało się go
 * porównać/zmigrować. Geometria zdefiniowana dla wszystkich breakpointów RGL, żeby uniknąć
 * skoków layoutu przy resize.
 *
 * Układ (lg, 12 kolumn):
 *  - Rząd 1: 4 karty statystyk (przychód, dokumenty, czas akceptacji, aktywni użytkownicy).
 *  - Rząd 2: przychody (obszar + porównanie rok do roku) · pracownicy wg firmy (donut) ·
 *            wydajność procesów.
 *  - Rząd 3: dokumenty w obiegu (słupki) · ostatnia aktywność · ostatnie transakcje.
 *  - Rząd 4: moje decyzje · powiadomienia · 3 kompaktowe kafelki.
 *  - Rząd 5: karty szybkich akcji + ostatni kontrahenci · karta profilu.
 * Węższe breakpointy (md/sm/xs) pakowane są automatycznie, w kolejności widżetów.
 */

import type {
  Breakpoint,
  DashboardLayoutDoc,
  DashboardWidget,
  WidgetLayoutItem,
  WidgetType,
} from './types';

type TranslateFn = (key: string, opts?: Record<string, unknown>) => string;

/** Specyfikacja jednego kafelka: położenie na lg + wysokość + szerokość na węższych ekranach. */
interface Slot {
  id: string;
  /** Typ — do doboru minimalnych rozmiarów. */
  type: WidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Szerokość (w kolumnach) na md / sm / xs; xs zawsze pełna szerokość. */
  md: number;
  sm: number;
}

const COLS: Record<Breakpoint, number> = { lg: 12, md: 10, sm: 6, xs: 4 };

const MIN: Partial<Record<WidgetType, { minW: number; minH: number }>> = {
  kpi: { minW: 2, minH: 2 },
  chart: { minW: 4, minH: 4 },
  flow: { minW: 4, minH: 4 },
  breakdown: { minW: 3, minH: 4 },
  process_performance: { minW: 3, minH: 4 },
  activity_feed: { minW: 3, minH: 3 },
  transactions: { minW: 3, minH: 3 },
  pending_approvals: { minW: 3, minH: 3 },
  alerts: { minW: 3, minH: 3 },
  recent_records: { minW: 3, minH: 3 },
  profile: { minW: 3, minH: 5 },
  quick_actions: { minW: 3, minH: 2 },
};

const ID = {
  kpiRevenue: 'kpi-sales-revenue',
  kpiDocuments: 'kpi-documents',
  kpiApproval: 'kpi-approval-time',
  kpiUsers: 'kpi-active-users',
  revenue: 'chart-revenue',
  breakdown: 'breakdown-employees',
  performance: 'process-performance',
  flow: 'flow-documents',
  activity: 'activity-feed',
  transactions: 'transactions',
  approvals: 'pending-approvals',
  alerts: 'alerts',
  tileInvoices: 'tile-purchase-invoices',
  tileContractors: 'tile-contractors',
  tileEmployees: 'tile-employees',
  quickActions: 'quick-actions',
  recent: 'recent-contractors',
  profile: 'profile',
} as const;

const SLOTS: Slot[] = [
  // Rząd 1 — karty statystyk
  { id: ID.kpiRevenue, type: 'kpi', x: 0, y: 0, w: 3, h: 2, md: 5, sm: 3 },
  { id: ID.kpiDocuments, type: 'kpi', x: 3, y: 0, w: 3, h: 2, md: 5, sm: 3 },
  { id: ID.kpiApproval, type: 'kpi', x: 6, y: 0, w: 3, h: 2, md: 5, sm: 3 },
  { id: ID.kpiUsers, type: 'kpi', x: 9, y: 0, w: 3, h: 2, md: 5, sm: 3 },
  // Rząd 2 — wykresy
  { id: ID.revenue, type: 'chart', x: 0, y: 2, w: 6, h: 5, md: 10, sm: 6 },
  { id: ID.breakdown, type: 'breakdown', x: 6, y: 2, w: 3, h: 5, md: 5, sm: 6 },
  { id: ID.performance, type: 'process_performance', x: 9, y: 2, w: 3, h: 5, md: 5, sm: 6 },
  // Rząd 3 — obieg dokumentów, aktywność, transakcje
  { id: ID.flow, type: 'flow', x: 0, y: 7, w: 6, h: 5, md: 10, sm: 6 },
  { id: ID.activity, type: 'activity_feed', x: 6, y: 7, w: 3, h: 5, md: 5, sm: 6 },
  { id: ID.transactions, type: 'transactions', x: 9, y: 7, w: 3, h: 5, md: 5, sm: 6 },
  // Rząd 4 — decyzje, powiadomienia, kompaktowe kafelki
  { id: ID.approvals, type: 'pending_approvals', x: 0, y: 12, w: 5, h: 6, md: 5, sm: 6 },
  { id: ID.alerts, type: 'alerts', x: 5, y: 12, w: 4, h: 6, md: 5, sm: 6 },
  { id: ID.tileInvoices, type: 'kpi', x: 9, y: 12, w: 3, h: 2, md: 5, sm: 3 },
  { id: ID.tileContractors, type: 'kpi', x: 9, y: 14, w: 3, h: 2, md: 5, sm: 3 },
  { id: ID.tileEmployees, type: 'kpi', x: 9, y: 16, w: 3, h: 2, md: 5, sm: 3 },
  // Rząd 5 — szybkie akcje + ostatni kontrahenci, profil
  { id: ID.quickActions, type: 'quick_actions', x: 0, y: 18, w: 9, h: 2, md: 10, sm: 6 },
  { id: ID.recent, type: 'recent_records', x: 0, y: 20, w: 9, h: 3, md: 10, sm: 6 },
  { id: ID.profile, type: 'profile', x: 9, y: 18, w: 3, h: 5, md: 10, sm: 6 },
];

/** Pakuje kafelki „półkami" od lewej do prawej na węższym breakpoincie (kolejność jak w SLOTS). */
function packRows(span: (s: Slot) => number, cols: number): WidgetLayoutItem[] {
  const out: WidgetLayoutItem[] = [];
  let x = 0;
  let y = 0;
  let shelfH = 0;
  for (const s of SLOTS) {
    const w = Math.min(span(s), cols);
    if (x + w > cols) {
      y += shelfH;
      x = 0;
      shelfH = 0;
    }
    const min = MIN[s.type] ?? { minW: 2, minH: 2 };
    out.push({ i: s.id, x, y, w, h: s.h, minW: Math.min(min.minW, w), minH: min.minH });
    x += w;
    shelfH = Math.max(shelfH, s.h);
  }
  return out;
}

function buildGeometry(): Record<Breakpoint, WidgetLayoutItem[]> {
  const lg: WidgetLayoutItem[] = SLOTS.map((s) => {
    const min = MIN[s.type] ?? { minW: 2, minH: 2 };
    return { i: s.id, x: s.x, y: s.y, w: s.w, h: s.h, minW: min.minW, minH: min.minH };
  });
  return {
    lg,
    md: packRows((s) => s.md, COLS.md),
    sm: packRows((s) => s.sm, COLS.sm),
    xs: packRows(() => COLS.xs, COLS.xs),
  };
}

export function buildDefaultLayout(t: TranslateFn): DashboardLayoutDoc {
  const metric = (m: string) => t(`tenant.dashboard.metrics.${m}`);

  const widgets: DashboardWidget[] = [
    {
      id: ID.kpiRevenue,
      type: 'kpi',
      config: { metric: 'sales_revenue', title: metric('sales_revenue') },
    },
    {
      id: ID.kpiDocuments,
      type: 'kpi',
      config: { metric: 'documents_total', title: metric('documents_total') },
    },
    {
      id: ID.kpiApproval,
      type: 'kpi',
      config: { metric: 'avg_approval_hours', title: metric('avg_approval_hours') },
    },
    {
      id: ID.kpiUsers,
      type: 'kpi',
      config: { metric: 'active_users', title: metric('active_users') },
    },
    {
      id: ID.revenue,
      type: 'chart',
      config: {
        entity: 'sale_invoices',
        measure: 'amount',
        compare: true,
        variant: 'area',
        title: t('tenant.dashboard.defaultTitles.revenue'),
      },
    },
    {
      id: ID.breakdown,
      type: 'breakdown',
      config: {
        metric: 'employees',
        dimension: 'company',
        title: t('tenant.dashboard.widgets.breakdown.titleFor', { name: metric('employees') }),
      },
    },
    {
      id: ID.performance,
      type: 'process_performance',
      config: { title: t('tenant.dashboard.widgets.process_performance.defaultTitle') },
    },
    {
      id: ID.flow,
      type: 'flow',
      config: { title: t('tenant.dashboard.widgets.flow.defaultTitle') },
    },
    {
      id: ID.activity,
      type: 'activity_feed',
      config: { limit: 7, title: t('tenant.dashboard.defaultTitles.recentActivity') },
    },
    {
      id: ID.transactions,
      type: 'transactions',
      config: { limit: 5, title: t('tenant.dashboard.widgets.transactions.defaultTitle') },
    },
    {
      id: ID.approvals,
      type: 'pending_approvals',
      config: { title: t('tenant.dashboard.defaultTitles.myTasks') },
    },
    {
      id: ID.alerts,
      type: 'alerts',
      config: { title: t('tenant.dashboard.widgets.alerts.defaultTitle') },
    },
    {
      id: ID.tileInvoices,
      type: 'kpi',
      config: { metric: 'purchase_invoices', variant: 'tile', title: metric('purchase_invoices') },
    },
    {
      id: ID.tileContractors,
      type: 'kpi',
      config: { metric: 'contractors', variant: 'tile', title: metric('contractors') },
    },
    {
      id: ID.tileEmployees,
      type: 'kpi',
      config: { metric: 'employees', variant: 'tile', title: metric('employees') },
    },
    {
      id: ID.quickActions,
      type: 'quick_actions',
      config: {
        title: t('tenant.dashboard.widgets.quick_actions.defaultTitle'),
        actions: [
          {
            label: t('tenant.dashboard.quickActions.newDocument'),
            description: t('tenant.dashboard.quickActions.newDocumentDesc'),
            to: '/sale-invoices/new',
            icon: 'FileText',
          },
          {
            label: t('tenant.dashboard.quickActions.newContractor'),
            description: t('tenant.dashboard.quickActions.newContractorDesc'),
            to: '/contractors/new',
            icon: 'Contact',
          },
          {
            label: t('tenant.dashboard.quickActions.inviteUser'),
            description: t('tenant.dashboard.quickActions.inviteUserDesc'),
            to: '/users',
            icon: 'UserCog',
          },
        ],
      },
    },
    {
      id: ID.recent,
      type: 'recent_records',
      config: {
        entity: 'contractors',
        limit: 4,
        title: t('tenant.dashboard.widgets.recent_records.defaultTitle'),
      },
    },
    {
      id: ID.profile,
      type: 'profile',
      config: { title: t('tenant.dashboard.widgets.profile.defaultTitle') },
    },
  ];

  return { version: 1, widgets, layouts: buildGeometry() };
}
